/**
 * Phase 3.10+ growth · population · town tile claims.
 *
 * Post-0.3.10 BINDING rewrite (playtest 2026-09-13):
 * - genPop = non-conscriptable workers; ableBodied = free conscriptable only
 * - settlementPop = genPop + ableBodied (growth + claim capacity base)
 * - Growth splits by genPop:ableBodied ratio; claims stepped by settlementPop
 * - Conscript leaves settlement; food mouths = settlement + garr + host
 *
 * - Settlement ladder: steading → … → royal seat
 * - Promote-or-spawn at borough floor (800); spawn-until seat ~1000
 */
import type { Season } from "./clock.ts";
import {
  cellKey,
  hexDistance,
  isDryLand,
  canSettleOn,
  parseKey,
  type Biome,
  type HexCell,
  type HexMap,
} from "./map.ts";
import { foodMidpoint, tileStatsFor, woodYield } from "./tileStats.ts";

export type SettlementTier =
  | "steading"
  | "hamlet"
  | "village"
  | "borough"
  | "market_town"
  | "shire_town"
  | "city"
  | "royal_seat";

export const TIER_ORDER: readonly SettlementTier[] = [
  "steading",
  "hamlet",
  "village",
  "borough",
  "market_town",
  "shire_town",
  "city",
  "royal_seat",
] as const;

/**
 * Inclusive population floors per tier.
 * steading floor 0 so hunger floor (pop 5) stays valid; display band ~20–49.
 */
export const TIER_POP_FLOOR: Record<SettlementTier, number> = {
  steading: 0,
  hamlet: 50,
  village: 150,
  borough: 800,
  market_town: 2000,
  shire_town: 5000,
  city: 12000,
  royal_seat: 35000,
};

/** Pop at which promote-or-spawn choice unlocks (borough floor). */
export const PROMOTE_OR_SPAWN_POP = 800;

/** Seat may still spawn a daughter while pending and under this pop. */
export const SPAWN_UNTIL_POP = 1000;

/** Population of a newly spawned steading. */
export const SPAWN_STEADING_POP = 24;

/** Max axial distance for a newly spawned steading from parent seat. */
export const SPAWN_VILLAGE_MAX_DISTANCE = 4;

/** High favor/loyalty for a freshly spawned bannerman-knight. */
export const SPAWN_BANNER_FAVOR_FLOOR = 75;

/**
 * Default able share of settlement when seeding a town.
 * ableBodied = free conscriptable only (not in garrison/host).
 */
export const ABLE_BODIED_FRAC = 0.22;
/** Soft settlement growth rate per growth tick (Spring/Autumn). */
export const GROWTH_RATE_CIVILIAN = 0.03;
/** Hard cumulative settlement growth cap per calendar year (fraction of year base). */
export const YEAR_GROWTH_CAP_CIVILIAN = 0.05;
/** Realm food cushion for +1 floor when otherwise stalled. */
export const GROWTH_FOOD_CUSHION = 50;
/** Autumn early-floor: min food / settlement to allow +1 when budget exhausted. */
export const AUTUMN_FLOOR_FOOD = 10;
export const AUTUMN_FLOOR_CIVILIAN = 5;

export interface TownState {
  /** Map cell key of this seat/town. */
  key: string;
  name: string;
  tier: SettlementTier;
  /**
   * Women, children, elderly — non-conscriptable civilians who still work.
   * Part of settlementPop with ableBodied.
   */
  genPop: number;
  /**
   * Conscriptable men not currently serving — free civilian workers only.
   * Conscript moves these into garrison (leaves settlement).
   */
  ableBodied: number;
  /**
   * @deprecated Prefer settlementPop(town). Kept as genPop+ableBodied mirror
   * for older call sites / tests during the rewrite.
   */
  population: number;
  /** Keys of claimed/worked hexes (including seat hex optional). */
  claimedKeys: string[];
  /** Thin growth encouragement — surplus food reinvested last tick. */
  growthPush: number;
  /** True when pop crossed promote/spawn gate and player hasn't chosen. */
  pendingPromoteOrSpawn: boolean;
  /** Settlement growth gained this calendar year (shared Spring+Autumn budget). */
  yearGrowthGained: number;
  /** Settlement base locked at year open (Winter→Spring) for the 5% cap. */
  yearGrowthCivBase: number;
}

export interface GrowthState {
  /** Player seat town (capital). */
  seat: TownState;
  /** Bannerman towns keyed by cell. */
  banners: TownState[];
  /** Last growth/claim log lines. */
  lastLog: string[];
}

/** Settlement souls = genPop + free ableBodied (claims + growth base). */
export function settlementPop(town: Pick<TownState, "genPop" | "ableBodied">): number {
  return Math.max(0, Math.floor(town.genPop) + Math.floor(town.ableBodied));
}

/**
 * Claim slot count from settlementPop.
 * Binding: 10 pop/tile until 50; 25 until 200; 100 until 500; continue stepped.
 *   0–50 → 1 per 10 (max 5 at 50)
 *   51–200 → +1 per 25
 *   201–500 → +1 per 100
 *   501+ → +1 per 250
 */
/** Claim slots from settlementPop — Explore-map SoT / growth.test lock. */
export function claimSlotCount(population: number): number {
  const p = Math.max(0, population);
  if (p < 20) return 2;
  if (p < 40) return 3;
  if (p < 70) return 5;
  if (p < 100) return 7;
  if (p < 150) return 9;
  return 12;
}

/** Claim radius (axial hexes from seat) — scales gently with settlementPop. */
export function claimRadius(population: number): number {
  const p = Math.max(0, population);
  if (p < 20) return 1;
  if (p < 50) return 2;
  if (p < 100) return 3;
  if (p < 200) return 4;
  if (p < 500) return 5;
  if (p < 2000) return 6;
  if (p < 5000) return 7;
  return 8;
}

export function tierForPopulation(population: number): SettlementTier {
  for (let i = TIER_ORDER.length - 1; i >= 0; i--) {
    const t = TIER_ORDER[i]!;
    if (population >= TIER_POP_FLOOR[t]) return t;
  }
  return "steading";
}

/** Pending + seat still under spawn-until cap → daughter spawn allowed. */
export function canSpawnDaughter(seat: TownState): boolean {
  return seat.pendingPromoteOrSpawn && settlementPop(seat) < SPAWN_UNTIL_POP;
}

/** Pending promote-or-spawn → in-place promote always allowed. */
export function canPromoteSeat(seat: TownState): boolean {
  return seat.pendingPromoteOrSpawn;
}

/** Seed able count from a settlement total (create / promote floors). */
export function ableBodiedForPop(population: number): number {
  return Math.max(0, Math.floor(Math.max(0, population) * ABLE_BODIED_FRAC));
}

/**
 * @deprecated Prefer settlementPop(town). Old signature was pop − military;
 * military no longer sits inside settlement — kept as settlement size passthrough
 * for UI during rewrite (military passed is ignored).
 */
export function civilianPopulation(
  population: number,
  _military?: number,
): number {
  return Math.max(0, Math.floor(population));
}

/** Keep population mirror in sync with gen+able. */
export function syncTownPopulation(town: TownState): TownState {
  const genPop = Math.max(0, Math.floor(town.genPop));
  const ableBodied = Math.max(0, Math.floor(town.ableBodied));
  const population = genPop + ableBodied;
  return {
    ...town,
    genPop,
    ableBodied,
    population,
    tier: tierForPopulation(population),
  };
}

/**
 * Split a growth gain across genPop and ableBodied by current ratio.
 * Empty settlement → use ABLE_BODIED_FRAC default split.
 * Playtest 0.3.11: always channel some growth into free ableBodied even when
 * free able is 0 (all garrisoned as men-at-arms) — teens / immigrants still arrive.
 */
export function splitGrowthByRatio(
  genPop: number,
  ableBodied: number,
  gain: number,
): { genDelta: number; ableDelta: number } {
  const g = Math.max(0, Math.floor(gain));
  if (g <= 0) return { genDelta: 0, ableDelta: 0 };
  const gen = Math.max(0, genPop);
  const able = Math.max(0, ableBodied);
  const total = gen + able;
  let ableDelta: number;
  if (total <= 0) {
    ableDelta = Math.floor(g * ABLE_BODIED_FRAC);
  } else {
    ableDelta = Math.floor((g * able) / total);
    // Floor: never deposit 0 able when there is growth (garrisoned-all case).
    if (ableDelta <= 0) {
      ableDelta = Math.max(1, Math.floor(g * ABLE_BODIED_FRAC));
      if (ableDelta > g) ableDelta = g;
    }
  }
  // Largest-remainder style: leftover to genPop (majority pool)
  const genDelta = g - ableDelta;
  return { genDelta, ableDelta };
}

export function createTownState(
  key: string,
  name: string,
  population = 28,
  claimedKeys: string[] = [],
): TownState {
  const pop = Math.max(0, Math.floor(population));
  const able = ableBodiedForPop(pop);
  const gen = Math.max(0, pop - able);
  const tier = tierForPopulation(pop);
  return {
    key,
    name,
    tier,
    genPop: gen,
    ableBodied: able,
    population: pop,
    claimedKeys: claimedKeys.length ? [...claimedKeys] : [key],
    growthPush: 0,
    pendingPromoteOrSpawn: false,
    yearGrowthGained: 0,
    yearGrowthCivBase: pop,
  };
}

export function createGrowthState(
  map: HexMap,
  opts?: { seatPop?: number; bannerPop?: number },
): GrowthState {
  const seatCell = map.cells.get(map.seatKey);
  const seatName = seatCell?.name ?? "Your Seat";
  const seatPop = opts?.seatPop ?? 32;
  const bannerPop = opts?.bannerPop ?? 22;

  const seat = createTownState(map.seatKey, seatName, seatPop, [map.seatKey]);
  // Auto-seed a few high-food claims near seat within radius
  seat.claimedKeys = autoSeedClaims(map, seat.key, seat.population);

  const banners: TownState[] = map.bannermanKeys.map((k) => {
    const c = map.cells.get(k);
    const t = createTownState(k, c?.name ?? "Banner", bannerPop, [k]);
    t.claimedKeys = autoSeedClaims(map, k, bannerPop);
    return t;
  });

  return {
    seat,
    banners,
    lastLog: [
      `Growth ready — seat settlement ${settlementPop(seat)} (${seat.tier}), claim ${claimSlotCount(settlementPop(seat))} hexes ≤r${claimRadius(settlementPop(seat))}.`,
    ],
  };
}

/** Prefer plains/river/coast inside radius; fill up to slot count. */
export function autoSeedClaims(
  map: HexMap,
  seatKey: string,
  population: number,
): string[] {
  const seat = parseKey(seatKey);
  const radius = claimRadius(population);
  const slots = claimSlotCount(population);
  const scored: { key: string; score: number; d: number }[] = [];

  for (const c of map.cells.values()) {
    if (!isDryLand(c.biome) && c.biome !== "river") continue;
    const d = hexDistance(seat.q, seat.r, c.q, c.r);
    if (d > radius) continue;
    const food = foodMidpoint(c.biome);
    const def = tileStatsFor(c.biome).defenseMod;
    // Prefer food, slight prefer closer, mild def
    const score = food * 10 - d * 2 + def;
    scored.push({ key: cellKey(c.q, c.r), score, d });
  }

  scored.sort((a, b) => b.score - a.score || a.d - b.d || a.key.localeCompare(b.key));
  const out: string[] = [];
  // Always include seat if dry/river
  const seatCell = map.cells.get(seatKey);
  if (seatCell && (isDryLand(seatCell.biome) || seatCell.biome === "river")) {
    out.push(seatKey);
  }
  for (const s of scored) {
    if (out.length >= slots) break;
    if (!out.includes(s.key)) out.push(s.key);
  }
  return out;
}

/**
 * Keys eligible to claim: within radius, not another settlement seat
 * (other towns' home hexes). Water is claimable (P3.8 modest fishery).
 * Slot cap is enforced in canClaimTile when adding.
 */
export function eligibleClaimKeys(
  map: HexMap,
  town: TownState,
  occupiedSeats: Set<string>,
): string[] {
  const seat = parseKey(town.key);
  const radius = claimRadius(settlementPop(town));
  const out: string[] = [];
  for (const c of map.cells.values()) {
    const k = cellKey(c.q, c.r);
    const d = hexDistance(seat.q, seat.r, c.q, c.r);
    if (d > radius) continue;
    // May keep own seat; may not steal another town's home hex
    if (occupiedSeats.has(k) && k !== town.key) continue;
    out.push(k);
  }
  return out;
}

export function canClaimTile(
  map: HexMap,
  town: TownState,
  targetKey: string,
  occupiedSeats: Set<string>,
): { ok: true } | { ok: false; reason: string } {
  const slots = claimSlotCount(settlementPop(town));
  if (town.claimedKeys.includes(targetKey)) {
    return { ok: false, reason: "Already claimed" };
  }
  if (town.claimedKeys.length >= slots) {
    return { ok: false, reason: `Claim slots full (${slots})` };
  }
  const eligible = eligibleClaimKeys(map, town, occupiedSeats);
  if (!eligible.includes(targetKey)) {
    return {
      ok: false,
      reason: `Outside claim radius r${claimRadius(settlementPop(town))} or blocked`,
    };
  }
  return { ok: true };
}

/**
 * How many claims over capacity (home hex never counts against excess drop).
 * Binding: after conscript, settlementPop drops → must unclaim before end turn.
 */
export function claimOverflow(town: TownState): number {
  const slots = claimSlotCount(settlementPop(town));
  return Math.max(0, town.claimedKeys.length - slots);
}

/**
 * Drop excess claims farthest from seat first (home hex protected).
 * Returns town + keys dropped (for UI/log).
 */
export function forceUnclaimToCapacity(town: TownState): {
  town: TownState;
  dropped: string[];
} {
  const slots = claimSlotCount(settlementPop(town));
  if (town.claimedKeys.length <= slots) {
    return { town, dropped: [] };
  }
  const seat = parseKey(town.key);
  const ranked = town.claimedKeys
    .filter((k) => k !== town.key)
    .map((k) => {
      const p = parseKey(k);
      return { k, d: hexDistance(seat.q, seat.r, p.q, p.r) };
    })
    .sort((a, b) => b.d - a.d || a.k.localeCompare(b.k));

  const keep = new Set<string>([town.key]);
  // Keep home + closest until slots filled
  const nonHome = town.claimedKeys.filter((k) => k !== town.key);
  const byNear = nonHome
    .map((k) => {
      const p = parseKey(k);
      return { k, d: hexDistance(seat.q, seat.r, p.q, p.r) };
    })
    .sort((a, b) => a.d - b.d || a.k.localeCompare(b.k));
  for (const row of byNear) {
    if (keep.size >= slots) break;
    keep.add(row.k);
  }
  // If home alone exceeds (slots>=1 always), still keep home
  const nextKeys = town.claimedKeys.filter((k) => keep.has(k));
  // Ensure home present
  if (!nextKeys.includes(town.key)) nextKeys.unshift(town.key);
  const dropped = town.claimedKeys.filter((k) => !keep.has(k) && k !== town.key);
  // Prefer ranked drop order for log stability
  dropped.sort((a, b) => {
    const da = ranked.find((r) => r.k === a)?.d ?? 0;
    const db = ranked.find((r) => r.k === b)?.d ?? 0;
    return db - da || a.localeCompare(b);
  });
  return {
    town: { ...town, claimedKeys: nextKeys },
    dropped,
  };
}

/** True when any town is over claim slots — block end turn until resolved. */
export function realmClaimOverflow(growth: GrowthState): number {
  let n = 0;
  for (const t of allTowns(growth)) n += claimOverflow(t);
  return n;
}

/**
 * Auto-trim every town to capacity (farthest first). Used if player confirms
 * force-resolve, or as a safety net — UI should prefer manual unclaim.
 */
export function forceUnclaimRealmToCapacity(growth: GrowthState): GrowthState {
  const seatRes = forceUnclaimToCapacity(growth.seat);
  // Banner force once (avoid double force)
  const bannerPairs = growth.banners.map((b) => forceUnclaimToCapacity(b));
  const bannersFinal = bannerPairs.map((p) => p.town);
  const dropLog = [
    ...seatRes.dropped.map((k) => `${growth.seat.name} drops claim ${k}`),
    ...bannerPairs.flatMap((p, i) =>
      p.dropped.map((k) => `${growth.banners[i]!.name} drops claim ${k}`),
    ),
  ];
  if (dropLog.length === 0) {
    return { ...growth, seat: seatRes.town, banners: bannersFinal };
  }
  return {
    ...growth,
    seat: seatRes.town,
    banners: bannersFinal,
    lastLog: [
      `Forced unclaim (${dropLog.length}): ${dropLog.slice(0, 4).join("; ")}${dropLog.length > 4 ? "…" : ""}`,
      ...growth.lastLog,
    ].slice(0, 12),
  };
}

export function claimTile(
  map: HexMap,
  town: TownState,
  targetKey: string,
  occupiedSeats: Set<string>,
): TownState {
  const check = canClaimTile(map, town, targetKey, occupiedSeats);
  if (!check.ok) return town;
  return {
    ...town,
    claimedKeys: [...town.claimedKeys, targetKey],
  };
}

export function unclaimTile(town: TownState, targetKey: string): TownState {
  // Never drop the home hex
  if (targetKey === town.key) return town;
  return {
    ...town,
    claimedKeys: town.claimedKeys.filter((k) => k !== targetKey),
  };
}

/** Food from claimed tiles (sum of midpoints) — tile half of prod. */
export function foodFromClaims(map: HexMap, claimedKeys: string[]): number {
  let sum = 0;
  for (const k of claimedKeys) {
    const c = map.cells.get(k);
    if (!c) continue;
    sum += foodMidpoint(c.biome);
  }
  return Math.round(sum * 10) / 10;
}

/**
 * P3.7 / playtest 0.3.11: claimed tiles yield wood via woodYield midpoints
 * (forest 7.5, hills/foothills 1.5). Unique keys — no double-count.
 */
export function woodFromClaims(map: HexMap, claimedKeys: string[]): number {
  let n = 0;
  for (const k of new Set(claimedKeys)) {
    const c = map.cells.get(k);
    if (!c) continue;
    n += woodYield(c.biome);
  }
  return Math.round(n * 10) / 10;
}

/** Realm-wide wood from all towns' claims (unique keys — no double-count). */
export function realmWoodProduction(map: HexMap, growth: GrowthState): number {
  const keys = new Set<string>();
  for (const t of allTowns(growth)) {
    for (const k of t.claimedKeys) keys.add(k);
  }
  return woodFromClaims(map, [...keys]);
}


/**
 * Strategic deposit bonuses from claimed tiles (playtest 0.3.11 midpoints).
 * Iron → arms 5–7 → 6; gold → gold 2–25 → 13.5; horses → horses +2 (unchanged).
 */
export function resourceFlagYields(
  map: HexMap,
  claimedKeys: string[],
): { arms: number; gold: number; horses: number; labels: string[] } {
  let arms = 0;
  let gold = 0;
  let horses = 0;
  const labels: string[] = [];
  // Unique keys so overlapping claim lists never double-pay a deposit
  for (const k of new Set(claimedKeys)) {
    const c = map.cells.get(k);
    if (!c?.resourceFlag) continue;
    if (c.resourceFlag === "iron") {
      arms += 6;
      labels.push(`iron@${k}`);
    } else if (c.resourceFlag === "gold") {
      gold += 13.5;
      labels.push(`gold@${k}`);
    } else if (c.resourceFlag === "horses") {
      horses += 2;
      labels.push(`horses@${k}`);
    }
  }
  return { arms, gold, horses, labels };
}

/**
 * Pop allocatable factor — kept for UI/tests; P3.8 food prod is tiles-only.
 * (Was: each 20 pop ~1 food effort. Lock: settlement + claimed tiles only.)
 */
export function popFoodFactor(population: number): number {
  return Math.floor(Math.max(0, population) / 20);
}

/**
 * Food production = settlement tile + claimed tiles only (no free pop factor).
 * Seat/home hex is always included even if missing from claimedKeys.
 */
export function townFoodProduction(map: HexMap, town: TownState): number {
  const keys = new Set(town.claimedKeys);
  keys.add(town.key);
  return Math.round(foodFromClaims(map, [...keys]));
}

/**
 * Seasonal growth: surplus food nudge + slow natural growth.
 * Marks pendingPromoteOrSpawn when crossing city gate first time.
 *
 * Post-0.3.10: military is NOT inside settlement. GrowthArmsCtx is optional
 * chrome only (mouths / logging); growth math uses settlementPop.
 */
export interface GrowthArmsCtx {
  /** Seat garrison + host (food mouths; not settlement). */
  seatMilitary: number;
  /** Banner military by town key (garrisoned + optional sent). */
  bannerMilitary?: Record<string, number>;
}

/**
 * Apply seasonal population growth to seat + banners.
 * Growth seasons: Spring + Autumn only.
 * Base = settlementPop (genPop + ableBodied); soft 3%/tick; hard 5%/year.
 * Gains split across genPop:ableBodied by current ratio.
 * Year counters reset on Spring (first tick of new year).
 */
export function applyGrowthTick(
  growth: GrowthState,
  season: Season,
  realmFood: number,
  armsCtx?: GrowthArmsCtx,
): GrowthState {
  const isGrowthSeason = season === "Spring" || season === "Autumn";
  const isNewYear = season === "Spring";
  const log: string[] = [];
  const seatMil = Math.max(0, armsCtx?.seatMilitary ?? 0);
  const banMil = armsCtx?.bannerMilitary ?? {};

  const tickTown = (
    town: TownState,
    military: number,
    label: string,
  ): TownState => {
    let t = syncTownPopulation({
      ...town,
      claimedKeys: [...town.claimedKeys],
    });

    if (isNewYear) {
      const base0 = settlementPop(t);
      t = { ...t, yearGrowthGained: 0, yearGrowthCivBase: base0 };
    }

    if (!isGrowthSeason) {
      return t;
    }

    const settle = settlementPop(t);
    // Hunger mouths = settlement + military still drawing hearth food
    const mouths = settle + Math.max(0, military);
    const hunger = hungerTag(realmFood, mouths);
    const yearBase = Math.max(0, t.yearGrowthCivBase);
    const yearCap = Math.floor(yearBase * YEAR_GROWTH_CAP_CIVILIAN);
    const remaining = Math.max(0, yearCap - t.yearGrowthGained);

    if (hunger === "starving") {
      const loss = Math.min(2, settle);
      if (loss > 0) {
        const { genDelta, ableDelta } = splitGrowthByRatio(
          t.genPop,
          t.ableBodied,
          loss,
        );
        // splitGrowthByRatio returns positive deltas — subtract for loss
        t = syncTownPopulation({
          ...t,
          genPop: Math.max(0, t.genPop - genDelta),
          ableBodied: Math.max(0, t.ableBodied - ableDelta),
        });
        // settle - newSettle should equal loss; fix drift
        let drift = settlementPop(t) - (settle - loss);
        while (drift > 0 && t.genPop > 0) {
          t = { ...t, genPop: t.genPop - 1 };
          drift--;
        }
        while (drift > 0 && t.ableBodied > 0) {
          t = { ...t, ableBodied: t.ableBodied - 1 };
          drift--;
        }
        t = syncTownPopulation(t);
        log.push(
          `${label} starves (−${loss} settlement → ${settlementPop(t)}).`,
        );
      }
      return t;
    }

    if (hunger === "hungry") {
      if (t.growthPush > 0) t = { ...t, growthPush: 0 };
      log.push(
        `${label} hungry — growth stalled (settlement ${settle}).`,
      );
      return t;
    }

    let gain = 0;
    if (settle > 0 && remaining > 0) {
      let raw = Math.floor(settle * GROWTH_RATE_CIVILIAN);
      if (t.growthPush > 0) raw += t.growthPush;
      if (raw <= 0 && realmFood >= GROWTH_FOOD_CUSHION && settle > 0) {
        raw = 1;
      }
      gain = Math.max(0, Math.min(raw, remaining));
    }

    const autumnFloor =
      gain === 0 &&
      season === "Autumn" &&
      remaining <= 0 &&
      realmFood >= AUTUMN_FLOOR_FOOD &&
      settle >= AUTUMN_FLOOR_CIVILIAN &&
      (hunger === "stable" || hunger === "surplus");
    if (autumnFloor) {
      gain = 1;
    }

    if (gain > 0) {
      const { genDelta, ableDelta } = splitGrowthByRatio(
        t.genPop,
        t.ableBodied,
        gain,
      );
      const pushSpent = t.growthPush;
      t = syncTownPopulation({
        ...t,
        genPop: t.genPop + genDelta,
        ableBodied: t.ableBodied + ableDelta,
        yearGrowthGained: t.yearGrowthGained + gain,
        growthPush: 0,
      });
      const pushNote = pushSpent > 0 ? ` (push +${pushSpent})` : "";
      log.push(
        `${label} grows +${gain}${pushNote} → settlement ${settlementPop(t)} (gen ${t.genPop} · able ${t.ableBodied}) (year ${t.yearGrowthGained}/${yearCap}).`,
      );
    } else if (settle > 0) {
      if (t.growthPush > 0) t = { ...t, growthPush: 0 };
      log.push(
        `${label} growth capped or flat (year ${t.yearGrowthGained}/${yearCap}, settlement ${settle}).`,
      );
    } else if (t.growthPush > 0) {
      t = { ...t, growthPush: 0 };
    }

    if (settlementPop(t) >= PROMOTE_OR_SPAWN_POP && !t.pendingPromoteOrSpawn) {
      t = { ...t, pendingPromoteOrSpawn: true };
      log.push(
        `${label} reached promote/spawn gate (settlement ${settlementPop(t)}) — choose promote or spawn.`,
      );
    }

    return t;
  };

  const seat = tickTown(growth.seat, seatMil, growth.seat.name);
  const banners = growth.banners.map((b) =>
    tickTown(b, Math.max(0, banMil[b.key] ?? 0), b.name),
  );

  return {
    seat,
    banners,
    lastLog: [...log, ...growth.lastLog].slice(0, 12),
  };
}

export function promoteTown(town: TownState): TownState {
  const idx = TIER_ORDER.indexOf(town.tier);
  const next = TIER_ORDER[Math.min(idx + 1, TIER_ORDER.length - 1)]!;
  const floor = TIER_POP_FLOOR[next];
  let genPop = Math.max(0, Math.floor(town.genPop));
  let ableBodied = Math.max(0, Math.floor(town.ableBodied));
  let pop = genPop + ableBodied;
  if (pop < floor) {
    const need = floor - pop;
    const { genDelta, ableDelta } = splitGrowthByRatio(genPop, ableBodied, need);
    genPop += genDelta;
    ableBodied += ableDelta;
    pop = genPop + ableBodied;
  }
  return syncTownPopulation({
    ...town,
    tier: next,
    pendingPromoteOrSpawn: false,
    genPop,
    ableBodied,
    population: pop,
  });
}

/**
 * Find a dry cell within SPAWN_VILLAGE_MAX_DISTANCE for a new village.
 * Returns null if none free.
 */
export function findSpawnSite(
  map: HexMap,
  parentKey: string,
  taken: Set<string>,
): HexCell | null {
  const parent = parseKey(parentKey);
  const candidates: HexCell[] = [];
  for (const c of map.cells.values()) {
    if (!canSettleOn(c)) continue;
    const k = cellKey(c.q, c.r);
    if (taken.has(k)) continue;
    if (c.settlement !== "none") continue;
    const d = hexDistance(parent.q, parent.r, c.q, c.r);
    if (d < 2 || d > SPAWN_VILLAGE_MAX_DISTANCE) continue;
    candidates.push(c);
  }
  candidates.sort(
    (a, b) =>
      hexDistance(parent.q, parent.r, a.q, a.r) -
        hexDistance(parent.q, parent.r, b.q, b.r) ||
      a.q - b.q ||
      a.r - b.r,
  );
  return candidates[0] ?? null;
}

export function settlementSeats(growth: GrowthState, map?: HexMap): Set<string> {
  const s = new Set<string>();
  s.add(growth.seat.key);
  for (const b of growth.banners) s.add(b.key);
  // Phase 4.5 political keeps are not claimable home hexes
  if (map) {
    for (const p of map.politicalSeats) s.add(p.key);
  }
  return s;
}

/** Seat + banner town states (for production / deposit aggregation). */
export function allTowns(growth: GrowthState): TownState[] {
  return [growth.seat, ...growth.banners];
}

/** Defense chrome: best defenseMod among claimed tiles (holder advantage tease). */
export function townDefenseHint(map: HexMap, town: TownState): number {
  let best = 0;
  for (const k of town.claimedKeys) {
    const c = map.cells.get(k);
    if (!c) continue;
    best = Math.max(best, tileStatsFor(c.biome).defenseMod);
  }
  return best;
}

/**
 * Shortage / hunger tag for growth + chrome.
 * Accepts raw pop or TownState (UI passes town).
 * surplus ≥2× need · stable ≥need · hungry ≥½ need · else starving
 */
export type HungerTag = "surplus" | "stable" | "hungry" | "starving";
export function hungerTag(
  foodStock: number,
  townOrPop: TownState | number,
): HungerTag {
  // Prefer explicit mouths (number). TownState path = settlement only —
  // callers that include garrison/host should pass settle + garr + host.
  const pop =
    typeof townOrPop === "number" ? townOrPop : settlementPop(townOrPop);
  const need = Math.max(1, Math.ceil(Math.max(0, pop) / 4));
  if (foodStock >= need * 2) return "surplus";
  if (foodStock >= need) return "stable";
  if (foodStock >= Math.ceil(need / 2)) return "hungry";
  return "starving";
}

export function formatTier(t: SettlementTier): string {
  const labels: Record<SettlementTier, string> = {
    steading: "Steading",
    hamlet: "Hamlet",
    village: "Village",
    borough: "Borough",
    market_town: "Market Town",
    shire_town: "Shire-Town",
    city: "City",
    royal_seat: "Royal Seat",
  };
  return labels[t];
}

/** Biome list helper for tests. */
export function biomesInClaims(map: HexMap, claimedKeys: string[]): Biome[] {
  const out: Biome[] = [];
  for (const k of claimedKeys) {
    const c = map.cells.get(k);
    if (c) out.push(c.biome);
  }
  return out;
}

/** Spend food cushion to push growth next tick (thin spend ∝ pop). Cost scales with pop. */
export function encourageGrowthCost(population: number): number {
  return Math.max(4, Math.ceil(population / 10));
}

export function encourageGrowth(
  growth: GrowthState,
  foodStock: number,
): { growth: GrowthState; foodSpent: number; ok: boolean; reason?: string } {
  const cost = encourageGrowthCost(growth.seat.population);
  if (foodStock < cost) {
    return { growth, foodSpent: 0, ok: false, reason: `Need ${cost} food (have ${foodStock})` };
  }
  if (growth.seat.growthPush > 0) {
    return { growth, foodSpent: 0, ok: false, reason: "Growth already encouraged this season" };
  }
  const seat = { ...growth.seat, growthPush: 1 };
  return {
    growth: {
      ...growth,
      seat,
      lastLog: [
        `Encouraged growth at ${seat.name} (−${cost} food · +1 pop nudge next tick).`,
        ...growth.lastLog,
      ].slice(0, 12),
    },
    foodSpent: cost,
    ok: true,
  };
}

/** Toggle claim on seat town: claim if free+eligible, unclaim if already claimed (home protected). */
export function toggleSeatClaim(
  map: HexMap,
  growth: GrowthState,
  targetKey: string,
): { growth: GrowthState; ok: boolean; reason?: string; action?: "claim" | "unclaim" } {
  const seats = settlementSeats(growth, map);
  const town = growth.seat;
  if (town.claimedKeys.includes(targetKey)) {
    if (targetKey === town.key) {
      return { growth, ok: false, reason: "Cannot unclaim the seat hex" };
    }
    const next = unclaimTile(town, targetKey);
    return {
      growth: {
        ...growth,
        seat: next,
        lastLog: [`Unclaimed ${targetKey}.`, ...growth.lastLog].slice(0, 12),
      },
      ok: true,
      action: "unclaim",
    };
  }
  const check = canClaimTile(map, town, targetKey, seats);
  if (!check.ok) return { growth, ok: false, reason: check.reason };
  const next = claimTile(map, town, targetKey, seats);
  return {
    growth: {
      ...growth,
      seat: next,
      lastLog: [
        `Claimed ${targetKey} (${next.claimedKeys.length}/${claimSlotCount(next.population)}).`,
        ...growth.lastLog,
      ].slice(0, 12),
    },
    ok: true,
    action: "claim",
  };
}

/** Promote seat in place when pending. */
export function resolvePromoteSeat(growth: GrowthState): GrowthState {
  if (!growth.seat.pendingPromoteOrSpawn) return growth;
  const seat = promoteTown(growth.seat);
  return {
    ...growth,
    seat,
    lastLog: [
      `${seat.name} promoted in place → ${formatTier(seat.tier)} (pop ${seat.population}).`,
      ...growth.lastLog,
    ].slice(0, 12),
  };
}

/**
 * Spawn a new village near seat when pending. Adds to banners.
 * Caller should also register banner on call/economy if needed.
 */
export function resolveSpawnVillage(
  map: HexMap,
  growth: GrowthState,
  name = "New Steading",
): { growth: GrowthState; spawned: TownState | null; reason?: string } {
  if (!growth.seat.pendingPromoteOrSpawn) {
    return { growth, spawned: null, reason: "No pending promote-or-spawn" };
  }
  if (!canSpawnDaughter(growth.seat)) {
    return {
      growth,
      spawned: null,
      reason: `Seat pop ${growth.seat.population} ≥ ${SPAWN_UNTIL_POP} — promote in place only`,
    };
  }
  const taken = settlementSeats(growth, map);
  // Also treat claimed keys lightly — allow spawn on unclaimed dry land only via findSpawnSite
  const site = findSpawnSite(map, growth.seat.key, taken);
  if (!site) {
    return { growth, spawned: null, reason: "No free spawn site within range" };
  }
  const key = cellKey(site.q, site.r);
  const steading = createTownState(key, name, SPAWN_STEADING_POP, [key]);
  steading.claimedKeys = autoSeedClaims(map, key, steading.population);
  const seat = { ...growth.seat, pendingPromoteOrSpawn: false };
  return {
    growth: {
      seat,
      banners: [...growth.banners, steading],
      lastLog: [
        `Spawned ${name} at ${key} (pop ${steading.population}). Seat remains ${formatTier(seat.tier)}.`,
        ...growth.lastLog,
      ].slice(0, 12),
    },
    spawned: steading,
  };
}

/** Total food production from all towns' claims + pop factor. */
export function realmFoodProduction(map: HexMap, growth: GrowthState): number {
  // Unique keys across realm so overlapping claims never double-feed
  const keys = new Set<string>();
  for (const t of allTowns(growth)) {
    keys.add(t.key);
    for (const k of t.claimedKeys) keys.add(k);
  }
  return Math.round(foodFromClaims(map, [...keys]));
}
